import React, { useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, RefreshCw, AlertCircle, Eye, Clock, User, Tag as TagIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Blog } from '../../services/admin/adminService';
import { getImageUrl } from '../../utils/imageHelper';

const BlogDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBlogById(Number(id));
      setBlog(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch blog');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!blog) return;
    
    try {
      setDeleting(true);
      await adminService.deleteBlog(blog.id);
      toast.success('Blog deleted successfully!');
      navigate('/admin/blogs');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete blog');
    } finally {
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Blog not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-secondary-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Blog Details</h1>
            <p className="text-secondary-600 mt-1">View and manage blog information</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/admin/blogs/${blog.id}/edit`)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => setDeleteModal(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Blog Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {blog.image && (
          <div className="w-full h-64 overflow-hidden">
            <img
              src={getImageUrl(blog.image) || blog.image}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-secondary-900 mb-2">{blog.title}</h2>
              <p className="text-lg text-secondary-600">{blog.description}</p>
            </div>
          </div>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-600 mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <User size={16} />
              <span>{blog.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <TagIcon size={16} />
              <span>{blog.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} />
              <span>{blog.read_time} min read</span>
            </div>
            <span
              className={`px-3 py-1 text-xs rounded-full font-semibold ${
                blog.status === 'published'
                  ? 'bg-green-100 text-green-800'
                  : blog.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {blog.status.charAt(0).toUpperCase() + blog.status.slice(1)}
            </span>
            {Boolean(blog.featured) && (
              <span className="px-3 py-1 text-xs rounded-full font-semibold bg-primary-100 text-primary-800">
                Featured
              </span>
            )}
          </div>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-secondary-700 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {blog.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SEO Keywords */}
          {blog.seo_keywords && blog.seo_keywords.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-secondary-700 mb-2">SEO Keywords</h3>
              <div className="flex flex-wrap gap-2">
                {blog.seo_keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-secondary-900 mb-4">Content</h3>
        <div 
          className="prose prose-lg max-w-none blog-content text-secondary-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-secondary-600">Views</span>
              <span className="font-semibold text-secondary-900">{blog.views}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Likes</span>
              <span className="font-semibold text-secondary-900">{blog.likes}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Comments</span>
              <span className="font-semibold text-secondary-900">{blog.comments}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-600">Rating</span>
              <span className="font-semibold text-secondary-900">{blog.rating || '0.0'}</span>
            </div>
          </div>
        </div>

        {/* SEO & Publication Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">SEO & Publication</h3>
          <div className="space-y-3">
            {blog.meta_title && (
              <div>
                <label className="text-sm font-medium text-secondary-600">Meta Title</label>
                <p className="text-secondary-900 mt-1">{blog.meta_title}</p>
              </div>
            )}
            {blog.meta_description && (
              <div>
                <label className="text-sm font-medium text-secondary-600">Meta Description</label>
                <p className="text-secondary-900 mt-1">{blog.meta_description}</p>
              </div>
            )}
            {blog.slug && (
              <div>
                <label className="text-sm font-medium text-secondary-600">Slug</label>
                <p className="text-secondary-900 mt-1 font-mono text-sm">{blog.slug}</p>
              </div>
            )}
            {blog.published_by && (
              <div>
                <label className="text-sm font-medium text-secondary-600">Published By</label>
                <p className="text-secondary-900 mt-1">{blog.published_by}</p>
              </div>
            )}
            {blog.published_at && (
              <div>
                <label className="text-sm font-medium text-secondary-600">Published At</label>
                <p className="text-secondary-900 mt-1">
                  {new Date(blog.published_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dates & Technical Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Timeline & Technical</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">
              Created At
            </label>
            <p className="text-secondary-900">
              {new Date(blog.created_at).toLocaleString()}
            </p>
          </div>
          {blog.updated_at && (
            <div>
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Updated At
              </label>
              <p className="text-secondary-900">
                {new Date(blog.updated_at).toLocaleString()}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">IP Address</label>
            <p className="text-secondary-900 font-mono text-sm">{blog.ip_address}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">User Agent</label>
            <p className="text-secondary-900 text-xs break-all">{blog.user_agent}</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Blog</h3>
              </div>
              
              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the blog{' '}
                <strong>{blog.title}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetails;
